import React, { useState, useEffect, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import LoginScreen from './LoginScreen';
import ConfigurationScreen from './ConfigurationScreen';
import VocabularyScreen, { Vocable as VocabularyListVocable, clearVocabularyCache } from './VocabularyScreen';
import ViewVocableScreen from './ViewVocableScreen';
import AddEditVocableScreen, { AddEditVocableFormValues } from './AddEditVocableScreen';
import ImprintScreen from './ImprintScreen';
import StatsScreen from './StatsScreen';
import { authService } from '../services/authService';
import { configService, UserConfiguration, LanguageMode } from '../services/configService';
import { getLearningMethodShortLabel, getOutputModeShortLabel, getLanguageModeShortLabel } from '../utils/configLabels';
import { VERB_FORMS } from '../utils/verbFormLabels';
import { buildAnswerWithinWidth, isAnswerCorrect as evaluateAnswerCorrect, PromptMode } from '../utils/trainingAnswerUtils';
import { getVerbFormInfo, VERB_FORM_ORDER, getPartOfSpeechLabel, getVerbTypeLabel } from '../utils/vocabularyLabels';
import { authenticatedFetch } from '../utils/apiUtils';
import './App.css';

interface TrainingVocable {
  id: string;
  japanese: string;
  kana: string;
  english: string[];
  conjugations?: Record<string, string>;
  kanjiMapping?: Record<string, string>;
  username?: string;
  nonDesiredVocables?: string | null;
  focusIndex?: number;
}

interface TrainingPrompt {
  vocable: TrainingVocable;
  promptMode: PromptMode;
  englishPrompts?: string[];
}

type AppState = 'login' | 'start' | 'training' | 'configure';

function App() {
  const [appState, setAppState] = useState<AppState>('login');
  const [state, setState] = useState<'start' | 'training'>('start');
  const [trainingPrompts, setTrainingPrompts] = useState<TrainingPrompt[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentCorrectIndex, setCurrentCorrectIndex] = useState(0);
  const [currentPrompt, setCurrentPrompt] = useState<TrainingPrompt | null>(null);
  const [userInput, setUserInput] = useState('');
  const [showAnswer, setShowAnswer] = useState(false);
  const [isConjugationsOpen, setIsConjugationsOpen] = useState(false);
  const [isTrainingActionsMenuOpen, setIsTrainingActionsMenuOpen] = useState(false);
  const [isSearchOverlayOpen, setIsSearchOverlayOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<VocabularyListVocable[]>([]);
  const [allVocables, setAllVocables] = useState<VocabularyListVocable[]>([]);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [selectedSearchVocable, setSelectedSearchVocable] = useState<VocabularyListVocable | null>(null);
  const [isAnswerStatusTwoLines, setIsAnswerStatusTwoLines] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [correctCount, setCorrectCount] = useState(0);
  const [gradeButtonsState, setGradeButtonsState] = useState<'hidden' | 'correct' | 'incorrect'>('hidden');
  const [typoUsed, setTypoUsed] = useState(false);
  const [wasAnswerEmpty, setWasAnswerEmpty] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [flashcardCorrectSelected, setFlashcardCorrectSelected] = useState(false);
  const [isTrainingLoading, setIsTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState('');
  const [retryVocableIds, setRetryVocableIds] = useState<Set<string>>(new Set());
  const previousVocableIdRef = useRef<string | null>(null);
  const trainingPromptsRef = useRef<TrainingPrompt[]>([]);
  const [reviewResults, setReviewResults] = useState<{ id: string; grade: number }[]>([]);
  const reviewResultsRef = useRef<{ id: string; grade: number }[]>([]);
  const correctAnswerRef = useRef<HTMLDivElement | null>(null);
  const answerStatusRef = useRef<HTMLSpanElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [answerDisplayWidth, setAnswerDisplayWidth] = useState<number | null>(null);
  const [answerDisplayFont, setAnswerDisplayFont] = useState<string>(
    '600 14px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  );
  const trainingActionsMenuRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const reduceScriptSwitchingFirstHalfRef = useRef<boolean | null>(null);
  const previousStateRef = useRef<'start' | 'training'>('start');
  const previousShowConfigurationRef = useRef(false);
  const previousShowVocabularyRef = useRef(false);
  
  // Track window width for responsive mode detection
  const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
  
  // Helper function to determine if we should use reduced script switching
  // Yes if: outputMode is DEFAULT
  // Yes if: outputMode is FLASHCARD_MOBILE_DEFAULT_DESKTOP and isMobile (<=640px)
  // Otherwise no
  const shouldUseReduceScriptSwitching = (reduceSwitching: boolean, mode: string, outputMode: string | undefined, currentWindowWidth: number): boolean => {
    if (!reduceSwitching) return false;
    if (mode !== 'MIXED') return false;
    if (!outputMode) return false;
    
    if (outputMode === 'DEFAULT') {
      return true;
    }
    
    if (outputMode === 'FLASHCARD_MOBILE_DEFAULT_DESKTOP') {
      return currentWindowWidth > 640; // Mobile breakpoint from CSS
    }
    
    return false;
  };

  const createTrainingPrompts = useCallback((
    vocables: TrainingVocable[],
    languageMode: LanguageMode | undefined,
    reduceScriptSwitching: boolean | undefined,
    outputMode: string | undefined
  ): TrainingPrompt[] => {
    const mode = languageMode ?? 'MIXED';
    const reduceSwitching = reduceScriptSwitching ?? false;

    const pickEnglishPrompts = (vocable: TrainingVocable): string[] => {
      if (!vocable.english || vocable.english.length === 0) {
        return [];
      }
      // Pick 2 entries, or as many as available if less than 2
      const count = Math.min(2, vocable.english.length);
      // Shuffle and take first count entries
      const shuffled = [...vocable.english].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, count);
    };

    // If reduceScriptSwitching is enabled and mode is MIXED, split the vocables in half
    if (shouldUseReduceScriptSwitching(reduceSwitching, mode, outputMode, windowWidth)) {
      const totalPrompts = vocables.length;
      const halfPoint = Math.ceil(totalPrompts / 2);
      
      // Randomly choose which language comes first
      const firstLanguageIsEnglish = Math.random() < 0.5;
      reduceScriptSwitchingFirstHalfRef.current = firstLanguageIsEnglish;

      return vocables.map((vocable, index) => {
        let promptMode: PromptMode = 'JAPANESE';
        let englishPrompts: string[] = [];

        // Determine which language to use based on position
        const shouldUseEnglish = index < halfPoint 
          ? firstLanguageIsEnglish 
          : !firstLanguageIsEnglish;

        if (shouldUseEnglish) {
          const prompts = pickEnglishPrompts(vocable);
          if (prompts.length > 0) {
            promptMode = 'ENGLISH';
            englishPrompts = prompts;
          }
        }

        return {
          vocable,
          promptMode,
          englishPrompts,
        };
      });
    } else {
      reduceScriptSwitchingFirstHalfRef.current = null;
    }

    // Default behavior: random assignment for MIXED mode
    return vocables.map((vocable) => {
      let promptMode: PromptMode = 'JAPANESE';
      let englishPrompts: string[] = [];

      const shouldUseEnglish =
        mode === 'ENGLISH' ||
        mode === 'VERBS' ||
        (mode === 'MIXED' && Math.random() < 0.5);

      if (shouldUseEnglish) {
        const prompts = pickEnglishPrompts(vocable);
        if (prompts.length > 0) {
          promptMode = 'ENGLISH';
          englishPrompts = prompts;
        }
      }

      return {
        vocable,
        promptMode,
        englishPrompts,
      };
    });
  }, [windowWidth]);
  
  // Check if configuration screen should be shown
  const [showConfiguration, setShowConfiguration] = useState(false);
  
  // Check if vocabulary screen should be shown
  const [showVocabulary, setShowVocabulary] = useState(false);
  const [showAddEditVocable, setShowAddEditVocable] = useState(false);
  const [showViewVocable, setShowViewVocable] = useState(false);
  const [vocableToView, setVocableToView] = useState<VocabularyListVocable | null>(null);
  const [vocableToEdit, setVocableToEdit] = useState<VocabularyListVocable | null>(null);
  const [vocabularyRefreshToken, setVocabularyRefreshToken] = useState(0);
  const [returnToViewAfterEdit, setReturnToViewAfterEdit] = useState(false);
  
  // Imprint screen
  const [showImprintScreen, setShowImprintScreen] = useState(false);
  
  // Stats screen
  const [showStatsScreen, setShowStatsScreen] = useState(false);
  
  // Store current configuration
  const [config, setConfig] = useState<UserConfiguration | null>(null);

  // Load configuration
  const loadConfiguration = useCallback(async () => {
    try {
      const loadedConfig = await configService.getConfiguration();
      setConfig({
        ...loadedConfig,
        languageMode: loadedConfig.languageMode ?? 'MIXED',
      });
    } catch (err) {
      console.error('Failed to load configuration:', err);
      // Set default config if load fails
      setConfig({
        vocableNumber: 10,
        learningMethod: 'SPACED_REPETITION',
        outputMode: 'DEFAULT',
        languageMode: 'MIXED',
      });
    }
  }, []);

  // Check authentication on mount
  useEffect(() => {
    if (authService.isAuthenticated()) {
      setAppState('start');
      loadConfiguration();
    } else {
      setAppState('login');
    }
  }, [loadConfiguration]);

  // Check token expiration once when appState changes (not on login screen)
  useEffect(() => {
    if (appState === 'login') {
      return; // Don't check if already on login screen
    }

    // Check token expiration once
    if (!authService.isAuthenticated()) {
      // Token expired or invalid, redirect to login
      authService.removeToken();
      setAppState('login');
    }
  }, [appState]);

  // Track window width for responsive mode detection
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clear training error when navigating to start screen
  useEffect(() => {
    if (state === 'start' && previousStateRef.current !== 'start') {
      setTrainingError('');
    }
    previousStateRef.current = state;
  }, [state]);
  
  // Helper function to determine if we're in flashcard mode
  // For FLASHCARD_MOBILE_DEFAULT_DESKTOP: flashcard on mobile (<=640px), default on desktop (>640px)
  const isFlashcardMode = useMemo(() => {
    if (!config?.outputMode) return false;
    
    if (config.outputMode === 'FLASHCARD') {
      return true;
    }
    
    if (config.outputMode === 'FLASHCARD_MOBILE_DEFAULT_DESKTOP') {
      return windowWidth <= 640; // Mobile breakpoint from CSS
    }
    
    return false;
  }, [config?.outputMode, windowWidth]);

  // Clear training error when navigating back from configure or vocabulary screens
  useEffect(() => {
    // Clear error when navigating back from configure screen
    if (previousShowConfigurationRef.current && !showConfiguration && state === 'start') {
      setTrainingError('');
    }
    // Clear error when navigating back from vocabulary screen
    if (previousShowVocabularyRef.current && !showVocabulary && state === 'start') {
      setTrainingError('');
    }
    previousShowConfigurationRef.current = showConfiguration;
    previousShowVocabularyRef.current = showVocabulary;
  }, [showConfiguration, showVocabulary, state]);

  const getConfigSummary = (): string => {
    if (!config) return 'Loading configuration...';
    
    const methodLabel = getLearningMethodShortLabel(config.learningMethod);
    const modeLabel = getOutputModeShortLabel(config.outputMode);
    const languageLabel = getLanguageModeShortLabel(config.languageMode);

    
    return `${config.vocableNumber} • ${methodLabel} • ${modeLabel} • ${languageLabel}`;
  };

  const handleLoginSuccess = () => {
    setAppState('start');
    setState('start');
    setShowConfiguration(false);
    setShowVocabulary(false);
    setShowAddEditVocable(false);
    setShowViewVocable(false);
    loadConfiguration();
  };

  const handleLogout = () => {
    authService.removeToken();
    clearVocabularyCache();
    setAppState('login');
    setShowVocabulary(false);
    setShowAddEditVocable(false);
    setVocableToEdit(null);
    setTrainingPrompts([]);
    setCurrentIndex(0);
    setCurrentPrompt(null);
    setTrainingError('');
    setSessionComplete(false);
    setAnsweredCount(0);
    setCorrectCount(0);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
  };

  const handleStart = useCallback(async () => {
    setTrainingError('');
    setIsTrainingLoading(true);
    setSessionComplete(false);
    setAnsweredCount(0);
    setCorrectCount(0);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
    setWasAnswerEmpty(false);
    setFlashcardCorrectSelected(false);
    setRetryVocableIds(new Set());
    setReviewResults([]);
    reviewResultsRef.current = [];

    try {
      const response = await authenticatedFetch('/api/training/vocables');

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Failed to fetch training vocables');
      }

      const data: TrainingVocable[] = await response.json();

      if (!Array.isArray(data) || data.length === 0) {
        setTrainingPrompts([]);
        setCurrentPrompt(null);
        setTrainingError('No vocabulary available for training yet.');
        setState('start');
        return;
      }

      const prompts = createTrainingPrompts(data, config?.languageMode, config?.reduceScriptSwitching, config?.outputMode);

      if (prompts.length === 0) {
        setTrainingPrompts([]);
        setCurrentPrompt(null);
        setTrainingError('No vocabulary available for training yet.');
        setState('start');
        return;
      }

      setTrainingPrompts(prompts);
      initialPromptsCountRef.current = prompts.length;
      setCurrentCorrectIndex(0);
      setCurrentIndex(0);
      setCurrentPrompt(prompts[0]);
      setUserInput('');
      setShowAnswer(false);
      setIsCorrect(false);
      setState('training');
    } catch (error) {
      console.error('Failed to start training session', error);
      setTrainingError(
        error instanceof Error
          ? error.message
          : 'An unexpected error occurred while starting training.'
      );
      setTrainingPrompts([]);
      setCurrentPrompt(null);
      setState('start');
      setReviewResults([]);
      reviewResultsRef.current = [];
    } finally {
      setIsTrainingLoading(false);
    }
  }, [config, createTrainingPrompts]);

  const addVocableToPromptsAtRandom = (trainingPrompts: TrainingPrompt[], prompt: TrainingPrompt, currentPos: number): TrainingPrompt[] => {
    let currentPromptCopy: TrainingPrompt = {
      ...prompt,
    };

    const newPrompts = trainingPrompts; // Use prevPrompts instead of trainingPrompts
    // Calculate a random position after the current one, but within the remaining prompts
    const remainingPrompts = newPrompts.length - currentPos - 1;
    const randomOffset = remainingPrompts > 0 
      ? Math.floor(Math.random() * (remainingPrompts + 1))
      : 0;
    const insertPosition = currentPos + 1 + randomOffset;
    
    // If reduceScriptSwitching is enabled and languageMode is MIXED, determine which language to use
    if (config?.reduceScriptSwitching && config?.languageMode === 'MIXED' && trainingPrompts.length > 0) {
      const totalPrompts = initialPromptsCountRef.current || trainingPrompts.length;
      const halfPoint = Math.ceil(totalPrompts / 2);
      
      // Determine which language was used in the first half from the ref (set during prompt creation)
      const firstHalfLanguageIsEnglish = reduceScriptSwitchingFirstHalfRef.current ?? true;
      
      // Determine which half we're currently in based on currentCorrectIndex
      const isInFirstHalf = insertPosition < halfPoint;
      
      // Set the prompt mode based on which half we're in
      const shouldUseEnglish = isInFirstHalf 
        ? firstHalfLanguageIsEnglish 
        : !firstHalfLanguageIsEnglish;
      
      if (shouldUseEnglish) {
        // Pick English prompts if available
        const availableEnglishPrompts = prompt.vocable.english;
        if (availableEnglishPrompts && availableEnglishPrompts.length > 0) {
          // Pick 2 entries, or as many as available if less than 2
          const count = Math.min(2, availableEnglishPrompts.length);
          // Shuffle and take first count entries
          const shuffled = [...availableEnglishPrompts].sort(() => Math.random() - 0.5);
          const selectedPrompts = shuffled.slice(0, count);
          currentPromptCopy = {
            ...prompt,
            promptMode: 'ENGLISH',
            englishPrompts: selectedPrompts,
          };
        } else {
          // Fallback to Japanese if no English available
          currentPromptCopy = {
            ...prompt,
            promptMode: 'JAPANESE',
            englishPrompts: undefined,
          };
        }
      } else {
        // Use Japanese
        currentPromptCopy = {
          ...prompt,
          promptMode: 'JAPANESE',
          englishPrompts: undefined,
        };
      }
    }
    
    let updatedPrompts: TrainingPrompt[] = [];

      
    newPrompts.splice(insertPosition, 0, currentPromptCopy);
    updatedPrompts = newPrompts;
    
    setTrainingPrompts((prevPrompts) => {
      trainingPromptsRef.current = newPrompts; // Update ref immediately
      return newPrompts; // Return the new state
    });
    
    return updatedPrompts; // Return the updated prompts for immediate use
  };

  const submitReviewResults = useCallback(async () => {
    const payload = reviewResultsRef.current;
    if (!payload.length) {
      return;
    }

    try {
      const response = await authenticatedFetch('/api/training/review', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to apply vocable reviews', errorText || response.statusText);
        return;
      }

      setReviewResults([]);
      reviewResultsRef.current = [];
    } catch (error) {
      console.error('Error applying vocable reviews', error);
    }
  }, []);

  const handleCheckAnswer = () => {
    if (!currentPrompt || showAnswer || sessionComplete) {
      return;
    }

    if (isFlashcardMode) {
      // In flashcard mode, just show the Wrong/Correct buttons without evaluating
      setShowAnswer(true);
      setFlashcardCorrectSelected(false);
      setGradeButtonsState('hidden');
      return;
    }

    // Default mode: evaluate the answer
    const vocable = currentPrompt.vocable;
    const vocableId = vocable.id;
    const isRetryVocable = retryVocableIds.has(vocableId);
    const isEmpty = !userInput.trim();
    const includedConjugations = config?.includedConjugations;
    const isAnswerCorrect = evaluateAnswerCorrect(currentPrompt.promptMode, userInput, vocable, includedConjugations);

    setIsCorrect(isAnswerCorrect);
    setShowAnswer(true);
    setWasAnswerEmpty(isEmpty);
    
    setGradeButtonsState(isAnswerCorrect ? 'correct' : 'incorrect');
    setTypoUsed(false);
  };

  const advanceToNextVocable = (updatedPrompts?: TrainingPrompt[]) => {
    const promptsToUse = updatedPrompts ?? trainingPromptsRef.current; // Use passed prompts or fallback to ref
    const nextIndex = currentIndex + 1;

    if (nextIndex >= promptsToUse.length) {
      setSessionComplete(true);
      setGradeButtonsState('hidden');
      setTypoUsed(false);
      setWasAnswerEmpty(false);
      setFlashcardCorrectSelected(false);
      setUserInput('');
      return;
    }

    setCurrentIndex(nextIndex);
    setCurrentPrompt(promptsToUse[nextIndex]); // Use promptsToUse instead of trainingPrompts
    setShowAnswer(false);
    setUserInput('');
    setIsCorrect(false);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
    setWasAnswerEmpty(false);
    setFlashcardCorrectSelected(false);
  };

  const handleTypo = () => {
    if (!showAnswer || sessionComplete || gradeButtonsState !== 'incorrect' || typoUsed || !currentPrompt) {
      return;
    }

    const vocableId = currentPrompt.vocable.id;
    const isRetryVocable = retryVocableIds.has(vocableId);
    
    setGradeButtonsState('correct');
    setTypoUsed(true);
    setIsCorrect(true);
  };

  const handleGrade = (_grade: number) => {

    if (!showAnswer || sessionComplete || (gradeButtonsState === 'hidden' && !isFlashcardMode) || !currentPrompt) {
      return;
    }

    const vocableId = currentPrompt.vocable.id;
    var isRetryVocable = retryVocableIds.has(vocableId);
    var newPrompts = trainingPrompts;

    if(_grade >= 1) {
      setCurrentCorrectIndex((prev) => prev + 1);
    }

    // Only update counters and review results for non-retry vocables
    if (!isRetryVocable) {
      setAnsweredCount((prev) => prev + 1);
      if (_grade >= 1) {
        setCorrectCount((prev) => prev + 1);
      }

      setReviewResults((prev) => {
        const updated = [...prev, { id: vocableId, grade: _grade }];
        reviewResultsRef.current = updated;
        return updated;
      });
    } else {
      if(_grade >= 1) {
        setRetryVocableIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(vocableId);
          return newSet;
        });
      }

      // Remove all future occurrences of this vocable from the training prompts
        for (let i = newPrompts.length - 1; i > currentIndex; i--) {
          if (newPrompts[i].vocable.id === vocableId) {
            newPrompts.splice(i, 1);
          }
        }
        trainingPromptsRef.current = newPrompts; // Update ref immediately
      setTrainingPrompts((prev) => {
        trainingPromptsRef.current = newPrompts; // Update ref immediately
        return newPrompts;
      });

    }

    let latestPrompts: TrainingPrompt[] | undefined = undefined;

    // If incorrect and not a retry vocable yet, add to retry set and re-add to prompts
    if (_grade < 1 && !isRetryVocable) {
      setRetryVocableIds((prev) => new Set(prev).add(vocableId));
      newPrompts = addVocableToPromptsAtRandom(newPrompts, currentPrompt, currentIndex);
    }
    
    // If retry vocable is still incorrect, re-add to prompts to ask again
    if (_grade < 1 && isRetryVocable) {
      newPrompts = addVocableToPromptsAtRandom(newPrompts, currentPrompt, currentIndex);
    }
    
    // For retry vocables, don't persist grade, just advance
    if (isRetryVocable) {
      advanceToNextVocable(newPrompts); // Pass the updated prompts
      return;
    }

    // TODO: Persist grade using `_grade` value for non-retry vocables.

    advanceToNextVocable(newPrompts); // Pass the updated prompts (undefined if not updated)
  };

  // Auto-focus input field when a new vocable is shown
  useEffect(() => {
    if (state === 'training' && currentPrompt && !showAnswer && !isFlashcardMode && inputRef.current) {
      // Small delay to ensure the input is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
    }, [currentPrompt, showAnswer, state, isFlashcardMode]);


  const handleConfigure = () => {
    setShowConfiguration(true);
  };

  const handleBackFromConfiguration = () => {
    setShowConfiguration(false);
  };

  const handleConfigSaved = async () => {
    // Reload configuration after it's been saved
    await loadConfiguration();
  };

  const handleVocabulary = () => {
    setShowAddEditVocable(false);
    setShowViewVocable(false);
    setShowVocabulary(true);
    setVocableToView(null);
  };

  const handleBackFromVocabulary = () => {
    setShowVocabulary(false);
    setShowViewVocable(false);
    setShowAddEditVocable(false);
  };

  const handleOpenAddVocable = () => {
    setVocableToEdit(null);
    setVocableToView(null);
    setReturnToViewAfterEdit(false);
    setShowVocabulary(false);
    setShowAddEditVocable(true);
  };

  const handleOpenViewVocable = (vocable: VocabularyListVocable) => {
    setVocableToView(vocable);
    setShowVocabulary(false);
    setShowViewVocable(true);
    setReturnToViewAfterEdit(false);
  };

  const handleEditVocableFromView = (vocable: VocabularyListVocable) => {
    setVocableToEdit(vocable);
    setShowViewVocable(false);
    setShowAddEditVocable(true);
    setReturnToViewAfterEdit(true);
  };

  const handleCancelAddEditVocable = () => {
    setShowAddEditVocable(false);
    setVocableToEdit(null);

    if (returnToViewAfterEdit && vocableToView) {
      setShowViewVocable(true);
      setShowVocabulary(false);
    } else {
      setShowViewVocable(false);
      setVocableToView(null);
      setShowVocabulary(true);
    }

    setReturnToViewAfterEdit(false);
  };

  const handleSaveVocable = async (
    values: AddEditVocableFormValues,
    options?: { addAnother?: boolean }
  ): Promise<boolean> => {
    const isEditing = Boolean(vocableToEdit?.id);
    const endpoint = isEditing
      ? `/api/vocabulary/vocables/${vocableToEdit?.id}`
      : '/api/vocabulary/vocables';
    const method = isEditing ? 'PUT' : 'POST';

    const trimmedJapanese = values.japanese.trim();
    const trimmedKana = values.kana.trim();

    const payload: Record<string, unknown> = {
      japanese: trimmedJapanese,
      english: values.english,
      partOfSpeech: values.partOfSpeech,
    };

    if (trimmedKana) {
      payload.kana = trimmedKana;
    }

    if (isEditing && vocableToEdit?.id) {
      payload.id = vocableToEdit.id;
    }

    if (values.partOfSpeech === 'VERB') {
      if (values.verbType) {
        payload.verbType = values.verbType;
      }
      payload.transitive = Boolean(values.isTransitive);
    }

    try {
      const response = await authenticatedFetch(endpoint, {
        method,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `Failed to ${isEditing ? 'update' : 'create'} vocable`,
          errorText || response.statusText
        );
        
        // Handle duplicate vocable error (409 Conflict)
        if (response.status === 409) {
          throw new Error('An identical vocable already exists.');
        }
        
        // Handle other errors
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} vocable. Please try again.`);
      }

      let responseData: any = null;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        responseData = null;
      }

      if (options?.addAnother) {
        setVocabularyRefreshToken((prev) => prev + 1);
        setVocableToEdit(null);
        return true;
      }

      if (returnToViewAfterEdit && vocableToView) {
        const updatedVocable: VocabularyListVocable = {
          ...vocableToView,
          ...(responseData ?? {}),
          japanese: (responseData?.japanese ?? trimmedJapanese) as string,
          kana: (responseData?.kana ?? trimmedKana) as string,
          english: (responseData?.english ?? values.english) as string[],
          partOfSpeech: (responseData?.partOfSpeech ?? values.partOfSpeech) as string,
          verbType: (responseData?.verbType ?? values.verbType) as string | undefined,
          transitive:
            (typeof responseData?.transitive !== 'undefined'
              ? responseData.transitive
              : typeof responseData?.isTransitive !== 'undefined'
              ? responseData.isTransitive
              : values.isTransitive) ?? undefined,
        };

        setVocableToView(updatedVocable);
        setShowAddEditVocable(false);
        setShowViewVocable(true);
        setShowVocabulary(false);
        setVocableToEdit(null);
        setReturnToViewAfterEdit(false);
        setVocabularyRefreshToken((prev) => prev + 1);
      } else {
        setShowAddEditVocable(false);
        setShowViewVocable(false);
        setShowVocabulary(true);
        setVocableToEdit(null);
        setVocableToView(null);
        setReturnToViewAfterEdit(false);
        setVocabularyRefreshToken((prev) => prev + 1);
      }

      return true;
    } catch (error) {
      console.error(`Error while ${isEditing ? 'updating' : 'creating'} vocable`, error);
      // Re-throw the error so AddEditVocableScreen can display the specific error message
      throw error;
    }
  };

  const handlesubmitReviewResultsAndBackToStart = () => {
    submitReviewResults();
    setVocabularyRefreshToken((prev) => prev + 1);
    handleBackToStart();
  };

  // Keyboard shortcuts for grade buttons
  useEffect(() => {
    if (state !== 'training') {
      return;
    }

    const handleKeyPress = (e: KeyboardEvent) => {
      const isInputField = e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement;
      const isInputDisabled = isInputField && (e.target as HTMLInputElement | HTMLTextAreaElement).disabled;
      
      // Handle Enter key
      if (e.key === 'Enter') {
        // When session is complete, Enter triggers "Save and back" button
        if (sessionComplete) {
          e.preventDefault();
          handlesubmitReviewResultsAndBackToStart();
          return;
        }
        
        // In Flashcard mode, when answer is not shown, Enter should trigger Check button
        if (isFlashcardMode && !showAnswer) {
          e.preventDefault();
          handleCheckAnswer();
          return;
        }
        
        // For Enter key: only handle if input is disabled (answer already shown)
        // If input is enabled, let the input's handler deal with it (to check answer)
        if (isInputField && !isInputDisabled) {
          return;
        }
        
        // When answer is shown, Enter triggers Easy or Forgot
        if (showAnswer) {
          e.preventDefault();
          // In flashcard mode, when Correct/Wrong buttons are shown
          if (isFlashcardMode && !flashcardCorrectSelected) {
            // Enter triggers Correct button
            setFlashcardCorrectSelected(true);
          } else if (wasAnswerEmpty) {
            // Forgot button when answer was empty
            handleGrade(0);
          } else if (gradeButtonsState === 'correct') {
            // Easy button
            handleGrade(3);
          } else if (gradeButtonsState === 'incorrect') {
            // Forgot button
            handleGrade(0);
          } else if (isFlashcardMode && flashcardCorrectSelected) {
            // Easy button in flashcard mode
            handleGrade(3);
          }
        }
        return;
      }
      
      // When session is complete, only handle Enter key
      if (sessionComplete) {
        return;
      }
      
      // For other keys, only handle if answer is shown
      if (!showAnswer) {
        return;
      }
      
      // For other keys, only handle if not typing in an enabled input field
      if (isInputField && !isInputDisabled) {
        return;
      }

      // Handle "1" key - Hard, Forgot, or Wrong
      if (e.key === '1') {
        e.preventDefault();
        // In flashcard mode, when Correct/Wrong buttons are shown
        if (isFlashcardMode && showAnswer && !flashcardCorrectSelected) {
          // Wrong button
          handleGrade(0);
        } else if (wasAnswerEmpty) {
          // Forgot button when answer was empty
          handleGrade(0);
        } else if (gradeButtonsState === 'correct') {
          // Hard button
          handleGrade(1);
        } else if (gradeButtonsState === 'incorrect') {
          // Forgot button
          handleGrade(0);
        } else if (isFlashcardMode && flashcardCorrectSelected) {
          // Hard button in flashcard mode
          handleGrade(1);
        }
      }

      // Handle "2" key - Easy, Typo, or Correct
      if (e.key === '2') {
        e.preventDefault();
        // In flashcard mode, when Correct/Wrong buttons are shown
        if (isFlashcardMode && showAnswer && !flashcardCorrectSelected) {
          // Correct button
          setFlashcardCorrectSelected(true);
        } else if (gradeButtonsState === 'correct') {
          // Easy button
          handleGrade(3);
        } else if (gradeButtonsState === 'incorrect' && !typoUsed) {
          // Typo button
          handleTypo();
        } else if (isFlashcardMode && flashcardCorrectSelected) {
          // Easy button in flashcard mode
          handleGrade(3);
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [state, showAnswer, sessionComplete, gradeButtonsState, isFlashcardMode, flashcardCorrectSelected, typoUsed, wasAnswerEmpty, handleGrade, handleTypo, handleCheckAnswer, setFlashcardCorrectSelected, handlesubmitReviewResultsAndBackToStart]);

  const handleBackToStart = () => {
    setState('start');
    setCurrentPrompt(null);
    setTrainingPrompts([]);
    setCurrentIndex(0);
    setTrainingError('');
    setUserInput('');
    setShowAnswer(false);
    setIsCorrect(false);
    setSessionComplete(false);
    setAnsweredCount(0);
    setCorrectCount(0);
    setGradeButtonsState('hidden');
    setTypoUsed(false);
    setWasAnswerEmpty(false);
    setFlashcardCorrectSelected(false);
    setRetryVocableIds(new Set());
    setReviewResults([]);
    reviewResultsRef.current = [];
    previousVocableIdRef.current = null;
  };

  const totalAnswered = answeredCount;
  const totalCorrect = correctCount;
  const accuracyPercentage = totalAnswered > 0 ? Math.round((totalCorrect / totalAnswered) * 100) : 0;
  const isSummaryVisible = sessionComplete && showAnswer;
  const currentVocable = currentPrompt?.vocable ?? null;
  const isVerbVocable = !!(currentVocable?.conjugations && Object.keys(currentVocable.conjugations).length > 0);
  const currentPromptMode: PromptMode = currentPrompt?.promptMode ?? 'JAPANESE';
  const isRetryVocable = currentVocable ? retryVocableIds.has(currentVocable.id) : false;
  const isTrainingWideLayout = windowWidth >= 600;

  const sortedConjugations = useMemo(() => {
    const conjugations = currentVocable?.conjugations ?? {};
    const conjugationEntries = Object.entries(conjugations).filter(([, value]) => Boolean(value));

    return conjugationEntries
      .map(([form, value]) => ({ form, value }))
      .sort((a, b) => {
        const aIndex = VERB_FORM_ORDER.indexOf(a.form as typeof VERB_FORM_ORDER[number]);
        const bIndex = VERB_FORM_ORDER.indexOf(b.form as typeof VERB_FORM_ORDER[number]);
        const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        if (safeA !== safeB) {
          return safeA - safeB;
        }
        return a.form.localeCompare(b.form);
      });
  }, [currentVocable?.id, currentVocable?.conjugations]);

  useEffect(() => {
    // Close the dialog when moving on / hiding the answer
    if (!showAnswer) {
      setIsConjugationsOpen(false);
    }
  }, [showAnswer, currentVocable?.id]);

  useEffect(() => {
    if (!isConjugationsOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsConjugationsOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isConjugationsOpen]);

  useEffect(() => {
    if (!isTrainingActionsMenuOpen) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (!trainingActionsMenuRef.current?.contains(target)) {
        setIsTrainingActionsMenuOpen(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsTrainingActionsMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('touchstart', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('touchstart', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isTrainingActionsMenuOpen]);

  useEffect(() => {
    if (isTrainingWideLayout) {
      setIsTrainingActionsMenuOpen(false);
    }
  }, [isTrainingWideLayout]);
  
  // Calculate training progress
  const initialPromptsCountRef = useRef<number>(0);
  const totalPrompts = initialPromptsCountRef.current;

  const currentProgress = totalPrompts > 0
    ? Math.round(((currentCorrectIndex) / totalPrompts) * 100)
    : 0;

  // Search functionality for training screen
  const loadAllVocables = useCallback(async () => {
    if (allVocables.length > 0) return; // Already loaded

    setIsSearchLoading(true);
    try {
      const response = await authenticatedFetch('/api/vocabulary/vocables');
      if (response.ok) {
        const data = await response.json();
        setAllVocables(data);
      }
    } catch (error) {
      console.error('Error loading vocabulary for search:', error);
    } finally {
      setIsSearchLoading(false);
    }
  }, [allVocables.length]);

  const handleOpenSearch = useCallback(() => {
    setIsSearchOverlayOpen(true);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedSearchVocable(null);
    loadAllVocables();
    // Focus the search input after the overlay is rendered
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  }, [loadAllVocables]);

  const handleCloseSearch = useCallback(() => {
    setIsSearchOverlayOpen(false);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedSearchVocable(null);
  }, []);

  // Filter vocables based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = allVocables.filter((v) => {
      const matchesJapanese = v.japanese?.includes(searchTerm);
      const matchesKana = v.kana?.includes(searchTerm);
      const matchesEnglish = v.english?.some((meaning) =>
        meaning.toLowerCase().includes(lowerSearch)
      );
      const matchesConjugations = v.conjugations
        ? Object.values(v.conjugations).some((conjugation) =>
            conjugation?.toLowerCase().includes(lowerSearch)
          )
        : false;
      return Boolean(matchesJapanese || matchesKana || matchesEnglish || matchesConjugations);
    });
    setSearchResults(filtered);
  }, [searchTerm, allVocables]);

  // Close search overlay on Escape key
  useEffect(() => {
    if (!isSearchOverlayOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedSearchVocable) {
          setSelectedSearchVocable(null);
        } else {
          handleCloseSearch();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isSearchOverlayOpen, selectedSearchVocable, handleCloseSearch]);

  // Track current vocable ID changes
  useEffect(() => {
    if (currentVocable) {
      previousVocableIdRef.current = currentVocable.id;
    } else {
      previousVocableIdRef.current = null;
    }
  }, [currentVocable]);

  // Keep trainingPromptsRef in sync with trainingPrompts state
  useEffect(() => {
    trainingPromptsRef.current = trainingPrompts;
  }, [trainingPrompts]);

  useEffect(() => {
    reviewResultsRef.current = reviewResults;
  }, [reviewResults]);
  const englishPrompts = currentPrompt?.englishPrompts ?? [];
  const englishPrompt = englishPrompts.length > 0 ? englishPrompts.join(', ') : '';
  const isEnglishPrompt = currentPromptMode === 'ENGLISH';
  const nonDesiredVocables = currentVocable?.nonDesiredVocables?.trim();
  const promptDisplayText = isEnglishPrompt ? englishPrompt : currentVocable?.japanese ?? '';
  const shouldUseCompactVocableSize =
    promptDisplayText.length > (isEnglishPrompt ? 16 : 13);
  const japaneseAnswerParts = useMemo(() => {
    if (!currentVocable) {
      return [];
    }

    return [currentVocable.japanese, currentVocable.kana].filter(
      (value): value is string => Boolean(value && value.trim().length > 0)
    );
  }, [currentVocable]);
  const englishAnswerParts = useMemo(() => {
    if (!currentVocable) {
      return [];
    }
    return (currentVocable.english ?? []).filter((value) => Boolean(value && value.trim().length > 0));
  }, [currentVocable]);
  const fullJapaneseAnswerText = japaneseAnswerParts.join(', ');
  const fullEnglishAnswerText = englishAnswerParts.join(', ');
  const summaryText = `You answered ${totalCorrect} of ${totalAnswered} correctly (${accuracyPercentage}%).`;
  const correctAnswerPlaceholder =
    isEnglishPrompt && nonDesiredVocables
      ? `Answer is not ${nonDesiredVocables}`
      : 'Correct answer will appear here';
  const shouldTruncateAnswers =
    Boolean(answerDisplayWidth && answerDisplayWidth > 0) && showAnswer && !isSummaryVisible && Boolean(currentVocable);
  const truncatedJapaneseAnswerText = shouldTruncateAnswers
    ? buildAnswerWithinWidth(japaneseAnswerParts, answerDisplayWidth, answerDisplayFont)
    : fullJapaneseAnswerText;
  const truncatedEnglishAnswerText = shouldTruncateAnswers
    ? buildAnswerWithinWidth(englishAnswerParts, answerDisplayWidth, answerDisplayFont)
    : fullEnglishAnswerText;
  const fullAnswerText = isSummaryVisible
    ? summaryText
    : showAnswer && currentVocable
      ? isEnglishPrompt
        ? fullJapaneseAnswerText || correctAnswerPlaceholder
        : fullEnglishAnswerText || correctAnswerPlaceholder
      : correctAnswerPlaceholder;

  const showVerbModeText = config?.languageMode === 'VERBS';
  const showSpecificVerbModeText = showVerbModeText && config?.includedConjugations && config.includedConjugations.length <= 3;

  const verbModeText = showSpecificVerbModeText ? `Allowed conjugations: ${config?.includedConjugations?.map(conj => {
    const form = VERB_FORMS.find(f => f.value === conj);
    return form ? form.englishName : conj;
  }).join(', ')}` : `${config?.includedConjugations?.length} conjugations are allowed`;
  
  const displayedCorrectAnswerText = isSummaryVisible
    ? summaryText
    : showVerbModeText ? verbModeText : showAnswer && currentVocable
      ? isEnglishPrompt
        ? truncatedJapaneseAnswerText || fullJapaneseAnswerText || correctAnswerPlaceholder
        : truncatedEnglishAnswerText || fullEnglishAnswerText || correctAnswerPlaceholder
      : correctAnswerPlaceholder;

  useLayoutEffect(() => {
    const updateMeasurements = () => {
      const element = correctAnswerRef.current;

      if (!element) {
        setAnswerDisplayWidth(null);
        return;
      }

      setAnswerDisplayWidth(element.clientWidth);
      
      // In FLASHCARD mode, use answer-status font style
      let computed: CSSStyleDeclaration;
      if (config?.outputMode === 'FLASHCARD') {
        const answerStatusElement = element.parentElement?.querySelector('.answer-status');
        computed = answerStatusElement 
          ? window.getComputedStyle(answerStatusElement)
          : window.getComputedStyle(element);
      } else {
        computed = window.getComputedStyle(element);
      }
      
      const fontValue =
        computed.font && computed.font !== 'normal normal normal 16px / normal serif'
          ? computed.font
          : `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`;
      setAnswerDisplayFont(fontValue);
    };

    updateMeasurements();
    window.addEventListener('resize', updateMeasurements);
    return () => {
      window.removeEventListener('resize', updateMeasurements);
    };
  }, [showAnswer, isSummaryVisible, currentVocable, isFlashcardMode]);

  useLayoutEffect(() => {
    const element = answerStatusRef.current;
    if (!element) {
      setIsAnswerStatusTwoLines(false);
      return;
    }

    const computed = window.getComputedStyle(element);
    const parsedLineHeight = parseFloat(computed.lineHeight);
    const parsedFontSize = parseFloat(computed.fontSize);
    const lineHeight =
      Number.isFinite(parsedLineHeight) && parsedLineHeight > 0
        ? parsedLineHeight
        : Number.isFinite(parsedFontSize) && parsedFontSize > 0
          ? parsedFontSize * 1.25
          : 0;

    const height = element.getBoundingClientRect().height;
    const isTwoLines = lineHeight > 0 && height > lineHeight * 1.5;
    setIsAnswerStatusTwoLines(isTwoLines);
  }, [windowWidth, showAnswer, isSummaryVisible, isFlashcardMode, isCorrect, displayedCorrectAnswerText]);

  // Show login screen if not authenticated
  if (appState === 'login') {
    return <LoginScreen onLoginSuccess={handleLoginSuccess} />;
  }

  // Show stats screen
  if (showStatsScreen) {
    return <StatsScreen onBack={() => setShowStatsScreen(false)} />;
  }

  // Show imprint screen
  if (showImprintScreen) {
    return <ImprintScreen onBack={() => setShowImprintScreen(false)} />;
  }

  if (showAddEditVocable) {
    return (
      <AddEditVocableScreen
        onBack={handleCancelAddEditVocable}
        onSave={handleSaveVocable}
        initialVocable={vocableToEdit ?? undefined}
      />
    );
  }

  if (showViewVocable && vocableToView) {
    const handleDeleteVocable = async () => {
      if (!vocableToView?.id) {
        return;
      }

      try {
        const response = await authenticatedFetch(
          `/api/vocabulary/${vocableToView.id}`,
          { method: 'DELETE' }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Failed to delete vocable', errorText || response.statusText);
          alert('Failed to delete vocable. Please try again.');
          return;
        }

        setShowViewVocable(false);
        setVocableToView(null);
        setShowVocabulary(true);
        setVocabularyRefreshToken((prev) => prev + 1);
      } catch (error) {
        console.error('Error while deleting vocable', error);
        alert('Failed to delete vocable. Please try again.');
      }
    };

    return (
      <ViewVocableScreen
        vocable={vocableToView}
        onBack={() => {
          setShowViewVocable(false);
          setVocableToView(null);
          setShowVocabulary(true);
        }}
        onEdit={handleEditVocableFromView}
        onDelete={handleDeleteVocable}
      />
    );
  }

  // Show configuration screen
  if (showConfiguration) {
    return <ConfigurationScreen onBack={handleBackFromConfiguration} onLogout={handleLogout} onConfigSaved={handleConfigSaved} />;
  }

  // Show vocabulary screen
  if (showVocabulary) {
    return (
      <VocabularyScreen
        onBack={handleBackFromVocabulary}
        onAddVocable={handleOpenAddVocable}
        onViewVocable={handleOpenViewVocable}
        refreshToken={vocabularyRefreshToken}
      />
    );
  }

  return (
    <div className="app">
      <main className="main-content">
        {state === 'start' && (
          <div className="start-screen">
            <h1 className="app-title">Vocabulary Trainer</h1>
            <p className="app-subtitle">{getConfigSummary()}</p>
            {trainingError && <div className="error-message">{trainingError}</div>}
            <div className="button-row">
              <button className="btn btn-secondary" onClick={handleConfigure}>
                Configure
              </button>
              <button className="btn btn-secondary" onClick={handleVocabulary}>
                Vocabulary
              </button>
              <button
                className="btn btn-primary"
                onClick={handleStart}
                disabled={isTrainingLoading}
              >
                {isTrainingLoading ? 'Starting...' : 'Start'}
              </button>
            </div>
            <hr style={{ margin: '24px 0', border: 'none', borderTop: '1px solid var(--border-color)' }} />
            <div style={{ marginTop: '-5px', marginBottom: '-18px', display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="imprint-button"
                onClick={() => setShowStatsScreen(true)}
              >
                Stats
              </button>
              <button
                type="button"
                className="imprint-button"
                onClick={() => setShowImprintScreen(true)}
              >
                About & Impressum
              </button>
            </div>
          </div>
        )}

        {state === 'training' && (
          <div className="training-screen">
            <div className="training-header">
              <div className="header-bar">
                <button className="back-button" onClick={handleBackToStart}>
                  ← Back
                </button>
                {totalPrompts > 0 && (
                  <div className="progress-bar-container">
                    <div className="progress-bar">
                      <div 
                        className="progress-bar-fill" 
                        style={{ width: `${currentProgress}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                {currentVocable && (
                  isTrainingWideLayout ? (
                    <div className="training-actions-inline">
                      <button
                        className="training-action-button"
                        type="button"
                        onClick={handleOpenSearch}
                        title="Search vocabulary"
                      >
                        Search
                      </button>
                      {(
                        <button
                          className="training-action-button"
                          type="button"
                          onClick={() => setIsConjugationsOpen(true)}
                          disabled={!(showAnswer && !sessionComplete)}
                          title={showAnswer ? 'Show verb forms' : 'Answer first to see forms'}
                        >
                          Forms
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="training-actions-menu" ref={trainingActionsMenuRef}>
                      <button
                        className={`training-actions-menu-button ${isTrainingActionsMenuOpen ? 'active' : ''}`}
                        type="button"
                        aria-haspopup="menu"
                        aria-expanded={isTrainingActionsMenuOpen}
                        aria-label="Training actions"
                        onClick={() => setIsTrainingActionsMenuOpen((v) => !v)}
                      >
                        Actions
                      </button>
                      {isTrainingActionsMenuOpen && (
                        <div className="training-actions-menu-dropdown" role="menu">
                          <button
                            className="training-actions-menu-item"
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              handleOpenSearch();
                              setIsTrainingActionsMenuOpen(false);
                            }}
                          >
                            Search
                          </button>

                          {isVerbVocable && (
                            <button
                              className="training-actions-menu-item"
                              type="button"
                              role="menuitem"
                              onClick={() => {
                                setIsConjugationsOpen(true);
                                setIsTrainingActionsMenuOpen(false);
                              }}
                              disabled={!(showAnswer && !sessionComplete)}
                            >
                              Forms
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>

            {isConjugationsOpen && isVerbVocable && currentVocable?.conjugations && (
              <div
                className="conjugations-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="Verb conjugations"
                onClick={() => setIsConjugationsOpen(false)}
              >
                <div className="conjugations-dialog" onClick={(e) => e.stopPropagation()}>
                  <h3 className="conjugations-title">Conjugations</h3>
                  <div className="conjugations-subtitle">
                    {currentVocable.japanese}
                    {currentVocable.kana && currentVocable.kana !== currentVocable.japanese ? ` (${currentVocable.kana})` : ''}
                  </div>

                  {sortedConjugations.length === 0 ? (
                    <div className="conjugations-empty">No conjugations available.</div>
                  ) : (
                    <div className="training-conjugations-scroll">
                      <div className="conjugations-table-wrapper">
                        <table className="conjugations-table">
                          <thead>
                            <tr>
                              <th>Form</th>
                              <th>Conjugation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedConjugations.map(({ form, value }) => {
                              const info = getVerbFormInfo(form);
                              return (
                                <tr key={form}>
                                  <td>
                                    <div className="conjugation-form-label">{info.label}</div>
                                    {info.description && (
                                      <div className="conjugation-form-description">{info.description}</div>
                                    )}
                                  </td>
                                  <td>{value || '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="conjugations-actions">
                    <button className="btn btn-primary" onClick={() => setIsConjugationsOpen(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Search Overlay */}
            {isSearchOverlayOpen && (
              <div
                className="search-overlay"
                role="dialog"
                aria-modal="true"
                aria-label="Search vocabulary"
                onClick={handleCloseSearch}
              >
                <div className="search-dialog" onClick={(e) => e.stopPropagation()}>
                  {selectedSearchVocable ? (
                    // Vocable detail view
                    <>
                      <div className="search-detail-header">
                        <button
                          className="search-back-button"
                          type="button"
                          onClick={() => setSelectedSearchVocable(null)}
                        >
                          ← Back to results
                        </button>
                      </div>
                      <div className="search-vocable-details">
                        <div className="search-detail-group">
                          <label className="search-detail-label">Japanese (Kanji)</label>
                          <div className="search-detail-value">{selectedSearchVocable.japanese || '—'}</div>
                        </div>
                        {selectedSearchVocable.kana && (
                          <div className="search-detail-group">
                            <label className="search-detail-label">Kana</label>
                            <div className="search-detail-value">{selectedSearchVocable.kana}</div>
                          </div>
                        )}
                        <div className="search-detail-group">
                          <label className="search-detail-label">English meanings</label>
                          <div className="search-detail-value">
                            {selectedSearchVocable.english?.length ? selectedSearchVocable.english.join(', ') : '—'}
                          </div>
                        </div>
                        {selectedSearchVocable.partOfSpeech && (
                          <div className="search-detail-group">
                            <label className="search-detail-label">Part of speech</label>
                            <div className="search-detail-value">
                              {getPartOfSpeechLabel(selectedSearchVocable.partOfSpeech) ?? selectedSearchVocable.partOfSpeech}
                              {selectedSearchVocable.partOfSpeech === 'VERB' && selectedSearchVocable.verbType &&
                                ` · ${getVerbTypeLabel(selectedSearchVocable.verbType) ?? selectedSearchVocable.verbType}`}
                            </div>
                          </div>
                        )}
                        {selectedSearchVocable.partOfSpeech === 'VERB' && typeof selectedSearchVocable.transitive === 'boolean' && (
                          <div className="search-detail-group">
                            <label className="search-detail-label">Transitive</label>
                            <div className="search-detail-value">
                              {selectedSearchVocable.transitive ? 'Yes' : 'No'}
                            </div>
                          </div>
                        )}
                        {selectedSearchVocable.partOfSpeech === 'VERB' && selectedSearchVocable.conjugations && Object.keys(selectedSearchVocable.conjugations).length > 0 && (
                          <div className="search-detail-group">
                            <label className="search-detail-label">Conjugations</label>
                            <div className="search-conjugations-scroll">
                              <div className="conjugations-table-wrapper">
                                <table className="conjugations-table">
                                  <thead>
                                    <tr>
                                      <th>Form</th>
                                      <th>Conjugation</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(selectedSearchVocable.conjugations)
                                      .filter(([, value]) => Boolean(value))
                                      .map(([form, value]) => ({ form, value }))
                                      .sort((a, b) => {
                                        const aIndex = VERB_FORM_ORDER.indexOf(a.form as typeof VERB_FORM_ORDER[number]);
                                        const bIndex = VERB_FORM_ORDER.indexOf(b.form as typeof VERB_FORM_ORDER[number]);
                                        const safeA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
                                        const safeB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
                                        return safeA !== safeB ? safeA - safeB : a.form.localeCompare(b.form);
                                      })
                                      .map(({ form, value }) => {
                                        const info = getVerbFormInfo(form);
                                        return (
                                          <tr key={form}>
                                            <td>
                                              <div className="conjugation-form-label">{info.label}</div>
                                              {info.description && (
                                                <div className="conjugation-form-description">{info.description}</div>
                                              )}
                                            </td>
                                            <td>{value || '—'}</td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="search-actions">
                        <button className="btn btn-primary" onClick={handleCloseSearch}>
                          Close
                        </button>
                      </div>
                    </>
                  ) : (
                    // Search list view
                    <>
                      <h3 className="search-title">Search Vocabulary</h3>
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="search-input"
                        placeholder="Search vocabulary..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <div className="search-results-container">
                        {isSearchLoading ? (
                          <div className="search-loading">Loading vocabulary...</div>
                        ) : searchTerm.trim() === '' ? (
                          <div className="search-hint">Type to search your vocabulary</div>
                        ) : searchResults.length === 0 ? (
                          <div className="search-empty">No vocabulary found matching "{searchTerm}"</div>
                        ) : (
                          <div className="search-results-list">
                            {searchResults.slice(0, 50).map((vocable) => (
                              <button
                                key={vocable.id}
                                type="button"
                                className="search-result-item"
                                onClick={() => setSelectedSearchVocable(vocable)}
                              >
                                <div className="search-result-japanese">{vocable.japanese}</div>
                                {vocable.kana !== vocable.japanese && (
                                  <div className="search-result-kana">{vocable.kana}</div>
                                )}
                                <div className="search-result-english">
                                  {vocable.english.join(', ')}
                                </div>
                              </button>
                            ))}
                            {searchResults.length > 50 && (
                              <div className="search-more-results">
                                Showing 50 of {searchResults.length} results
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="search-actions">
                        <button className="btn btn-primary" onClick={handleCloseSearch}>
                          Close
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {isTrainingLoading && (
              <div className="loading-state">Loading training session...</div>
            )}

            {!isTrainingLoading && currentVocable && (
              <>
                <div className="vocable-display">
                  <div className="prompt-wrapper">
                    <>
                      <div
                        className={`vocable-japanese ${
                          shouldUseCompactVocableSize ? 'vocable-japanese--compact' : ''
                        }`}
                      >
                        {promptDisplayText}
                      </div>
                      <div className="vocable-kana">
                        {(!showAnswer && !sessionComplete) || isEnglishPrompt
                          ? '\u3000'
                          : currentVocable.kana !== currentVocable.japanese
                            ? currentVocable.kana
                            : '\u3000'}
                      </div>
                    </>
                  </div>
                </div>

                <div
                  className={`answer-feedback ${
                    (isSummaryVisible || isFlashcardMode)
                      ? 'summary neutral'
                      : showAnswer
                        ? (isCorrect ? 'correct' : 'incorrect')
                        : 'neutral'
                  }`}
                >
                  <span className="answer-status" ref={answerStatusRef}>
                    {isSummaryVisible
                      ? 'Session Summary'
                      : showAnswer
                        ? (isFlashcardMode ? displayedCorrectAnswerText :isCorrect ? '✓ Correct!' : '✗ Incorrect')
                        : '⏳'}
                  </span>
                  {!isAnswerStatusTwoLines && (
                    <div className="correct-answer" ref={correctAnswerRef} title={fullAnswerText}>
                      {(isFlashcardMode && showAnswer && !isSummaryVisible) ? 'Was your answer correct?' :displayedCorrectAnswerText}
                    </div>
                  )}
                </div>

                

                {!isFlashcardMode && (
                  <div className="input-area">
                    <input
                      ref={inputRef}
                      type="text"
                      className="vocable-input"
                      placeholder={isEnglishPrompt ? 'Enter Japanese translation...' : 'Enter English translation...'}
                      value={userInput}
                      onChange={(e) => setUserInput(e.target.value)}
                      onCompositionStart={() => setIsComposing(true)}
                      onCompositionEnd={() => setIsComposing(false)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !showAnswer && !isComposing) {
                          e.stopPropagation(); // Prevent event from bubbling to window handler
                          handleCheckAnswer();
                        }
                      }}
                      disabled={showAnswer}
                      autoFocus
                    />
                  </div>
                )}

                <div className="button-row">
                  {sessionComplete ? (
                    <button className="btn btn-primary" onClick={handlesubmitReviewResultsAndBackToStart}>
                      Save and back
                    </button>
                  ) : !showAnswer ? (
                    <button
                      className="btn btn-primary"
                      onClick={handleCheckAnswer}
                    >
                      Check
                    </button>
                  ) : null}
                </div>

                {showAnswer && !sessionComplete && (
                  <div className="grade-buttons">
                    {isFlashcardMode ? (
                      flashcardCorrectSelected ? (
                        <>
                        {isRetryVocable ? (
                          <>
                            <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                              Continue
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="btn btn-primary" onClick={() => handleGrade(1)}>
                              Hard
                            </button>
                            <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                              Easy
                            </button>
                          </>
                        )}
                        </>
                      ) : (
                        <>
                          <button className="btn btn-primary" onClick={() => handleGrade(0)}>
                            Wrong
                          </button>
                          <button className="btn btn-primary" onClick={() => setFlashcardCorrectSelected(true)}>
                            Correct
                          </button>
                        </>
                      )
                    ) : (
                      <>
                        {wasAnswerEmpty ? (
                          <button className="btn btn-primary" onClick={() => handleGrade(0)}>
                            Forgot
                          </button>
                        ) : (
                          <>
                            {gradeButtonsState === 'correct' && (
                              <>
                                {isRetryVocable ? (
                                  <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                                    Continue
                                  </button>
                                ) : (
                                  <>
                                    <button className="btn btn-primary" onClick={() => handleGrade(1)}>
                                      Hard
                                    </button>
                                    <button className="btn btn-primary" onClick={() => handleGrade(3)}>
                                      Easy
                                    </button>
                                  </>
                                )}
                              </>
                            )}
                            {gradeButtonsState === 'incorrect' && (
                              <>
                                <button className="btn btn-primary" onClick={() => handleGrade(0)}>
                                  Forgot
                                </button>
                                <button className="btn btn-primary" onClick={handleTypo}>
                                  Typo
                                </button>
                              </>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
            )}

            {!isTrainingLoading && !currentVocable && (
              <div className="empty-state">
                No training vocables available. Return to start and try again later.
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
